//@flow
import React from 'react';

import type { EntitySettings } from 'common/models/internal/EntitySettings';
import { DocumentField } from 'common/models/internal/EntitySettings';

import type { List } from 'immutable';
import { DocumentWithTooltip } from 'common/components/core/document/DocumentWithPreview';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';

import I18nHelper from 'common/helpers/I18nHelper';

const nsTranslate = I18nHelper.translate.bind(null, [
    'entityManagement',
    'translations'
]);

export default class DocumentsColumnHelper {
    static processColumns(
        entitySettings: ?EntitySettings,
        columns: Array<Object>,
        settingsName: string
    ) {
        const settings = entitySettings?.get(settingsName);
        if (!settings) {
            return columns;
        }
        const { documentSettings } = settings;
        let modifiedColumns = [...columns];
        if (documentSettings) {
            (documentSettings: List<DocumentField>)
                .filter(field => !!field.type)
                .forEach(field => {
                    const {
                        type,
                        isExpiryDateApplicable,
                        isDocumentIdApplicable
                    } = field;
                    if (isDocumentIdApplicable) {
                        modifiedColumns = [
                            ...modifiedColumns,
                            {
                                headerText: nsTranslate(
                                    `documentsProperties.documentId`,
                                    { type }
                                ),
                                id: `${type}documentId`,
                                visible: true,
                                minWidth: 100,
                                sortable: false,
                                filterable: false,
                                property: `documents`,
                                getCustomFilterInfo: ({ filteredColumn }) => {
                                    return new FilterBuilder()
                                        .withName('documents')
                                        .withOperation(FilterOperation.Contains)
                                        .withValue({
                                            documentId: filteredColumn.value
                                        });
                                },
                                Cell: props => {
                                    const {
                                        original: { documents },
                                        tdProps: {
                                            rest: { theme }
                                        }
                                    } = props;
                                    const documentIds = documents
                                        ?.filter(
                                            document => document.type === type
                                        )
                                        .map(document => document.documentId);

                                    if (!documentIds?.size) {
                                        return <span>{'-'}</span>;
                                    }
                                    return (
                                        <span theme={theme}>
                                            {documentIds.get(0)}
                                        </span>
                                    );
                                }
                            }
                        ];
                    }
                    if (isExpiryDateApplicable) {
                        modifiedColumns = [
                            ...modifiedColumns,
                            {
                                headerText: nsTranslate(
                                    `documentsProperties.expiryDate`,
                                    { type }
                                ),
                                id: `${type}expiryDate`,
                                visible: true,
                                minWidth: 100,
                                sortable: false,
                                filterable: false,
                                property: `documents`,
                                getCustomFilterInfo: ({ filteredColumn }) => {
                                    return new FilterBuilder()
                                        .withName('documents')
                                        .withOperation(FilterOperation.Contains)
                                        .withValue({
                                            expiryDate: filteredColumn.value
                                        });
                                },
                                Cell: props => {
                                    const {
                                        original: { documents },
                                        tdProps: {
                                            rest: { theme }
                                        }
                                    } = props;
                                    const expiryDates = documents
                                        ?.filter(
                                            document => document.type === type
                                        )
                                        .map(document => document.expiryDate);

                                    if (!expiryDates?.size) {
                                        return <span>{'-'}</span>;
                                    }
                                    return (
                                        <span theme={theme}>
                                            {expiryDates.get(0)}
                                        </span>
                                    );
                                }
                            }
                        ];
                    }
                    modifiedColumns = [
                        ...modifiedColumns,
                        {
                            headerText: nsTranslate(
                                `documentsProperties.links`,
                                { type }
                            ),
                            id: `${type}Links`,
                            visible: true,
                            minWidth: 250,
                            sortable: false,
                            filterable: false,
                            property: `documents`,
                            Cell: props => {
                                const {
                                    original: { documents },
                                    tdProps: {
                                        rest: { theme }
                                    }
                                } = props;
                                const urls = documents
                                    ?.filter(document => document.type === type)
                                    .map(document => document.imageUrl);

                                if (!urls?.size) {
                                    return <span>{'-'}</span>;
                                }
                                return (
                                    <span theme={theme} className={theme.link}>
                                        {urls.map(src => {
                                            return (
                                                <DocumentWithTooltip
                                                    key={src}
                                                    src={src}
                                                    className={theme.imageLink}
                                                    theme={theme}
                                                />
                                            );
                                        })}
                                    </span>
                                );
                            }
                        }
                    ];
                });
        }
        return modifiedColumns;
    }
}
